import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTranslation, Trans } from 'react-i18next';
import { mdiClose } from '@mdi/js';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Zod from 'zod';

import {
    ITranslateAICourseMutation,
    ITranslateAICourseMutationVariables,
    TranslateAICourseDocument,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { IconButton } from 'common/components/IconButton';
import { AILanguageSelectField, TextField } from 'common/forms/Field';
import { useApolloError } from 'common/hooks/useApolloError';
import { AIUsageIndicator } from 'common/components/AIUsageIndicator';
import { Divider } from 'common/components/Divider';
import { Link } from 'common/components/Link';

import { CourseAITranslateTaskStatus } from './CourseAITranslateTaskStatus';

export interface IProps {
    courseId: string;
    open: boolean;
    onClose?(): void;
}

interface IFormValues {
    language: string;
    context?: string;
}

export const CourseAITranslateModal = ({ courseId, open, onClose }: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [watchingTaskId, setWatchingTaskId] = useState<string | undefined>();
    // Default to true to prevent submission before usage data is loaded
    const [limitReached, setLimitReached] = useState(true);

    const [translateAICourse, { loading: translateLoading }] = useMutation<
        ITranslateAICourseMutation,
        ITranslateAICourseMutationVariables
    >(TranslateAICourseDocument);

    const validationSchema = Zod.object({
        language: Zod.string().min(1, translate('validate.required')),
        context: Zod.string().optional(),
    });

    const { control, handleSubmit, reset, formState } = useForm<IFormValues>({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            language: 'English',
        },
    });

    const { isSubmitting } = formState;

    const handleTranslate: SubmitHandler<IFormValues> = async (values) => {
        try {
            const result = await translateAICourse({
                variables: {
                    courseId,
                    translation: {
                        language: values.language,
                        context: values.context,
                    },
                },
            });

            const { taskId } = result.data?.translateAICourse || {};

            if (taskId) {
                setWatchingTaskId(taskId);
            }
        } catch (error) {
            showApolloError(error);
        }
    };

    useEffect(() => {
        if (!open) {
            reset();
            setWatchingTaskId(undefined);
            setLimitReached(true);
        }
    }, [open, reset]);

    const isLoading = isSubmitting || translateLoading;

    return (
        <Dialog fullWidth maxWidth={false} open={open} onClose={onClose}>
            {open && (
                <DialogContent
                    sx={{
                        height: '100vh',
                        width: '100%',
                        py: 10,
                    }}
                >
                    <Box sx={{ margin: 'auto', width: '100%' }}>
                        <Box sx={{ position: 'absolute', right: 10, top: 10 }}>
                            <IconButton
                                color="inherit"
                                iconPath={mdiClose}
                                iconSizeSm="2.8rem"
                                size="large"
                                onClick={onClose}
                            />
                        </Box>
                        <Container maxWidth="md">
                            {watchingTaskId && (
                                <CourseAITranslateTaskStatus
                                    sx={{
                                        mt: 10,
                                        mx: 'auto',
                                        maxWidth: '400px',
                                    }}
                                    taskId={watchingTaskId}
                                    onClose={onClose}
                                    onRetry={() => setWatchingTaskId(undefined)}
                                />
                            )}

                            <Box
                                sx={{
                                    mt: 2,
                                    display: watchingTaskId ? 'none' : 'block',
                                }}
                            >
                                <Typography variant="h2">
                                    {translate('courseAITranslateModal.title')}
                                </Typography>
                                <Typography sx={{ mt: 2 }} variant="body1">
                                    {translate('courseAITranslateModal.text')}
                                </Typography>
                                <form onSubmit={handleSubmit(handleTranslate)}>
                                    <AILanguageSelectField
                                        control={control}
                                        name="language"
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        control={control}
                                        label={translate(
                                            'courseAITranslateModal.contextFieldLabel'
                                        )}
                                        minRows={3}
                                        name="context"
                                    />

                                    <Divider sx={{ my: 2 }} />

                                    <Typography
                                        sx={{
                                            fontSize: '1.4rem',
                                            lineHeight: 1.6,
                                        }}
                                        variant="body2"
                                    >
                                        <Trans
                                            components={{
                                                a: (
                                                    <Link
                                                        rel="noreferrer"
                                                        underline="always"
                                                    />
                                                ),
                                            }}
                                            i18nKey="ai.disclaimer"
                                        />
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap-reverse',
                                            alignItems: 'center',
                                            columnGap: 2,
                                            rowGap: 3,
                                            mt: 2,
                                        }}
                                    >
                                        <Button
                                            color="primary"
                                            disabled={isLoading || limitReached}
                                            type="submit"
                                            variant="contained"
                                        >
                                            {translate(
                                                'courseAITranslateModal.translateButton'
                                            )}
                                        </Button>
                                        <Button
                                            disabled={isLoading}
                                            onClick={onClose}
                                        >
                                            {translate(
                                                'courseAITranslateModal.cancelButton'
                                            )}
                                        </Button>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <AIUsageIndicator
                                            sx={{
                                                width: '100%',
                                                maxWidth: 350,
                                            }}
                                            onLimitReached={setLimitReached}
                                        />
                                    </Box>
                                </form>
                            </Box>
                        </Container>
                    </Box>
                </DialogContent>
            )}
        </Dialog>
    );
};
